import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Paper,
  Card,
  CardContent,
  Grid,
  alpha,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  fetchPermissions,
  createPermission,
  updatePermissionAction,
  deletePermission,
  Permission,
} from "./permissionsSlice";
import { toast } from "react-toastify";

const Permissions: React.FC = () => {
  const dispatch = useDispatch();
  const { permissions, loading, error } = useSelector((state: RootState) => state.permissions);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    description: "",
    resource: "",
    action: "" as Permission['action'],
    isActive: true,
  });

  useEffect(() => {
    dispatch(fetchPermissions());
  }, [dispatch]);

  const handleOpenDialog = (permission?: Permission) => {
    if (permission) {
      setEditingPermission(permission);
      setFormData({
        name: permission.name,
        displayName: permission.displayName,
        description: permission.description || "",
        resource: permission.resource,
        action: permission.action,
        isActive: permission.isActive,
      });
    } else {
      setEditingPermission(null);
      setFormData({
        name: "",
        displayName: "",
        description: "",
        resource: "",
        action: "" as Permission['action'],
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPermission(null);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.displayName || !formData.resource || !formData.action) {
      toast.error("All fields are required");
      return;
    }

    try {
      if (editingPermission) {
        const id = editingPermission._id || editingPermission.id;
        if (id) {
          dispatch(updatePermissionAction({ id, data: formData }));
        }
      } else {
        dispatch(createPermission(formData));
      }
      handleCloseDialog();
      setTimeout(() => dispatch(fetchPermissions()), 500);
    } catch (error) {
      console.error("Failed to save permission:", error);
    }
  };

  const handleDelete = async (permission: Permission) => {
    if (permission.isSystemPermission) {
      toast.error("Cannot delete system permission");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete "${permission.displayName}"? This action cannot be undone.`
      )
    ) {
      const id = permission._id || permission.id;
      if (id) {
        dispatch(deletePermission(id));
        setTimeout(() => dispatch(fetchPermissions()), 500);
      }
    }
  };

  if (loading && permissions.length === 0) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant='h4' fontWeight='700' gutterBottom>
            Permission Management
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const formatDate = (date: number | string | Date | undefined) => {
    if (!date) return "N/A";
    const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return "N/A";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: GridColDef[] = [
    {
      field: "displayName",
      headerName: "Permission",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
            <SecurityIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant='body2' fontWeight='600'>
              {params.value}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {params.row.name}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "resource",
      headerName: "Resource",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip label={params.value} size='small' color='info' variant='outlined' />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip label={params.value} size='small' color='secondary' variant='outlined' />
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant='body2' color='text.secondary' noWrap>
          {params.value || "No description"}
        </Typography>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          color={params.value ? "success" : "default"}
          size='small'
        />
      ),
    },
    {
      field: "isSystemPermission",
      headerName: "Type",
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "System" : "Custom"}
          color={params.value ? "warning" : "info"}
          size='small'
          variant='outlined'
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title='Edit Permission'>
              <EditIcon />
            </Tooltip>
          }
          label='Edit'
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title={params.row.isSystemPermission ? 'Cannot delete system permission' : 'Delete Permission'}>
              <DeleteIcon />
            </Tooltip>
          }
          label='Delete'
          onClick={() => handleDelete(params.row)}
          disabled={params.row.isSystemPermission}
        />,
      ],
    },
  ];

  const activePermissions = permissions.filter((p) => p.isActive).length;
  const systemPermissions = permissions.filter((p) => p.isSystemPermission).length;
  const resources = Array.from(new Set(permissions.map((p) => p.resource)));

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h4' fontWeight='700' gutterBottom>
              Permission Management
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Manage system permissions and access controls
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            Add Permission
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant='h4' fontWeight='bold'>
                      {permissions.length}
                    </Typography>
                    <Typography variant='body2' sx={{ opacity: 0.9 }}>
                      Total Permissions
                    </Typography>
                  </Box>
                  <SecurityIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant='h4' fontWeight='bold' color='success.main'>
                      {activePermissions}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Active Permissions
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant='h4' fontWeight='bold' color='warning.main'>
                      {systemPermissions}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      System Permissions
                    </Typography>
                  </Box>
                  <SecurityIcon sx={{ fontSize: 40, color: "warning.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant='h4' fontWeight='bold' color='info.main'>
                      {resources.length}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Resources
                    </Typography>
                  </Box>
                  <SecurityIcon sx={{ fontSize: 40, color: "info.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          rows={permissions}
          columns={columns}
          getRowId={(row) => row._id || row.id || String(Math.random())}
          paginationMode='client'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={permissions.length}
          loading={loading}
          autoHeight
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha("#667eea", 0.1),
              borderBottom: `2px solid ${alpha("#667eea", 0.2)}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
          }}
        />
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth='md' fullWidth>
        <DialogTitle>
          {editingPermission ? "Edit Permission" : "Create New Permission"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label='Name'
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase() })}
              disabled={!!editingPermission}
              required
              helperText="Unique identifier (lowercase, no spaces)"
            />
            <TextField
              label='Display Name'
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              required
            />
            <TextField
              label='Description'
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
            <TextField
              label='Resource'
              value={formData.resource}
              onChange={(e) => setFormData({ ...formData, resource: e.target.value.toLowerCase() })}
              disabled={!!editingPermission?.isSystemPermission}
              required
              helperText="Resource name (e.g., users, programs, resources)"
            />
            <FormControl fullWidth required>
              <InputLabel>Action</InputLabel>
              <Select
                value={formData.action}
                onChange={(e) => setFormData({ ...formData, action: e.target.value as Permission['action'] })}
                disabled={!!editingPermission?.isSystemPermission}
                label="Action"
              >
                <MenuItem value='create'>Create</MenuItem>
                <MenuItem value='read'>Read</MenuItem>
                <MenuItem value='update'>Update</MenuItem>
                <MenuItem value='delete'>Delete</MenuItem>
                <MenuItem value='manage'>Manage</MenuItem>
                <MenuItem value='assign'>Assign</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={formData.isActive ? "Active" : "Inactive"}
                color={formData.isActive ? "success" : "default"}
                onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                sx={{ cursor: "pointer" }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant='contained'>
            {editingPermission ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Permissions;

